import { apiClient, isHttpClientError } from "@/http";
import { apiRoutes } from "./apiRoutes";
import type { MemberRef } from "./member";

// Admin member↔training workspace 매핑. 개인(me) 스코프 read 는 이 매핑을 근거로 한다.
// Admin 이 후보 드롭다운(training 라이브 프록시)에서 workspace 를 고른다. 그 workspace 의
// id+name 을 member 에 스냅샷으로 저장한다. workspace 생성/rename 은 admin training-workspaces
// 경계가 처리한다. trainingGroup.ts 와 같은 client 패턴이다.

// 매핑 드롭다운 후보 1개(`GET /admin/training-workspaces`). id 를 매핑값으로 저장한다.
export type TrainingWorkspaceCandidate = {
  id: string;
  name: string;
};

// member 에 매핑된 workspace 1개. workspace_name 은 추가 시점의 스냅샷 이름이다.
export type MemberWorkspace = {
  training_workspace_id: string;
  workspace_name: string;
  created_at: string;
};

export type MemberWorkspaceCreateReq = {
  training_workspace_id: string;
  workspace_name: string;
};

export type TrainingWorkspaceCreateReq = {
  group_id: string;
  name: string;
  description?: string | null;
};

export type TrainingWorkspaceRenameReq = {
  name: string;
};

// 매핑 후보. training 라이브 프록시라 실패하면 upstream.* 오류가 올 수 있다. group_id 를 주면
// 한 group 으로 좁힌다. 주지 않으면 BE 가 registry 가 아는 모든 training group 에 fan-out 하고
// 중복을 제거한다.
export async function listTrainingWorkspaceCandidates(
  groupId?: string,
): Promise<TrainingWorkspaceCandidate[]> {
  const response = await apiClient.get<TrainingWorkspaceCandidate[]>(
    apiRoutes.admin.trainingWorkspaces,
    { params: groupId ? { group_id: groupId } : {} },
  );
  return response.data;
}

// training-api workspace 를 만들고 registry 표시명으로 응답받는다. backend 가 training 쪽 실제
// 이름 정책을 숨긴다.
export async function createTrainingWorkspace(
  payload: TrainingWorkspaceCreateReq,
): Promise<TrainingWorkspaceCandidate> {
  const response = await apiClient.post<TrainingWorkspaceCandidate>(
    apiRoutes.admin.trainingWorkspaces,
    payload,
  );
  return response.data;
}

// registry 표시명을 rename 한다. training-api 의 rename 지원 여부는 backend 경계가 처리한다.
export async function renameTrainingWorkspace(
  trainingWorkspaceId: string,
  payload: TrainingWorkspaceRenameReq,
): Promise<TrainingWorkspaceCandidate> {
  const response = await apiClient.patch<TrainingWorkspaceCandidate>(
    apiRoutes.admin.trainingWorkspaceDetail(trainingWorkspaceId),
    payload,
  );
  return response.data;
}

export async function listMemberWorkspaces(memberUuid: string): Promise<MemberWorkspace[]> {
  const response = await apiClient.get<MemberWorkspace[]>(
    apiRoutes.admin.memberTrainingWorkspaces(memberUuid),
  );
  return response.data;
}

// 선점을 부여하고 201 매핑을 받는다. 이미 선점된 workspace 는 409(isWorkspaceAlreadyClaimedError)다.
export async function addMemberWorkspace(
  memberUuid: string,
  payload: MemberWorkspaceCreateReq,
): Promise<MemberWorkspace> {
  const response = await apiClient.post<MemberWorkspace>(
    apiRoutes.admin.memberTrainingWorkspaces(memberUuid),
    payload,
  );
  return response.data;
}

export async function removeMemberWorkspace(
  memberUuid: string,
  trainingWorkspaceId: string,
): Promise<void> {
  await apiClient.delete(
    apiRoutes.admin.memberTrainingWorkspaceDetail(memberUuid, trainingWorkspaceId),
  );
}

// workspace 의 training 쪽 값 한 벌(이름 + 소속 group). snapshot 자리에서는 name 이 null 일 수
// 있다. 선점 시점에 이름 스냅샷이 없던 행이 그렇다. live 자리에서는 항상 값이 있다.
export type WorkspaceTrainingRef = {
  name: string | null;
  group_id: string;
};

// 선점 스냅샷이 training live 와 다른 workspace 1개. display_name 은 admin 이 정하는 registry
// 표시명이라 대조하지 않는다(표시용). 대조는 snapshot↔live 사이에서만 한다.
// live=null 이면 training 에서 사라진 workspace 다.
export type WorkspaceDrift = {
  training_workspace_id: string;
  owner: MemberRef;
  display_name: string;
  snapshot: WorkspaceTrainingRef;
  live: WorkspaceTrainingRef | null;
};

// 동기화 확인 결과(`GET /admin/training-workspaces/sync-status`). 존재/이름/group/미선점 4버킷이다.
export type WorkspaceSyncStatusView = {
  missing: WorkspaceDrift[];
  name_drift: WorkspaceDrift[];
  group_drift: WorkspaceDrift[];
  unclaimed: TrainingWorkspaceCandidate[];
};

// 재매핑 요청. member_uuid 를 생략하면 소유자는 그대로 두고 스냅샷만 live 로 갱신한다.
export type WorkspaceRemapReq = {
  member_uuid?: string;
};

export type WorkspaceRemapChangedField =
  | "owner"
  | "display_name"
  | "snapshot.name"
  | "snapshot.group_id";

// 선점 한 줄의 상태. 재매핑 전후 비교에 쓴다.
export type WorkspaceClaimView = {
  owner: MemberRef;
  display_name: string;
  snapshot: WorkspaceTrainingRef;
};

// 재매핑 결과. changed_fields 는 실제로 바뀐 항목만 담는다(화면 요약용).
export type WorkspaceRemapView = {
  training_workspace_id: string;
  before: WorkspaceClaimView;
  after: WorkspaceClaimView;
  changed_fields: WorkspaceRemapChangedField[];
};

// 선점 행과 training live 를 대조한다. 읽기 전용이라 아무것도 바꾸지 않는다.
export async function getTrainingWorkspaceSyncStatus(): Promise<WorkspaceSyncStatusView> {
  const response = await apiClient.get<WorkspaceSyncStatusView>(
    apiRoutes.admin.trainingWorkspacesSyncStatus,
  );
  return response.data;
}

// 선점 스냅샷을 live 로 갱신한다. member_uuid 를 주면 소유자도 이전한다. 소유는 derived 값이다.
// 그래서 이전은 그 workspace 의 과거·미래 세션과 eval 전체에 즉시 반영된다(백필 없음).
// 오류는 다음과 같다:
//   - 선점되지 않은 workspace: 404(isWorkspaceClaimNotFoundError)
//   - training 에서 사라진 workspace: 422(isWorkspaceNotInScopeError). 선점 해제로 안내한다.
export async function remapTrainingWorkspace(
  trainingWorkspaceId: string,
  payload: WorkspaceRemapReq = {},
): Promise<WorkspaceRemapView> {
  const response = await apiClient.post<WorkspaceRemapView>(
    apiRoutes.admin.trainingWorkspaceRemap(trainingWorkspaceId),
    payload,
  );
  return response.data;
}

// research 공용 등록 1건(`GET/POST /admin/training-shared-workspaces`). 선점자 없이 research 그룹
// 멤버 전원이 쓸 수 있는 workspace 다. Schedule 은 이 workspace 에서 만들어진다.
// training_group_id 는 등록 시점의 소속 group 스냅샷이다. workspace_name 은 조회할 때 registry
// 표시명(alias)을 join 한 값이다.
export type SharedWorkspace = {
  training_workspace_id: string;
  training_group_id: string;
  workspace_name: string;
  created_at: string;
};

export type SharedWorkspaceCreateReq = {
  training_workspace_id: string;
};

export async function listSharedWorkspaces(): Promise<SharedWorkspace[]> {
  const response = await apiClient.get<SharedWorkspace[]>(apiRoutes.admin.trainingSharedWorkspaces);
  return response.data;
}

// 공용 등록. 201 을 받는다. 서버가 alias를 자동 생성하고 소속 group을 판정한다.
export async function addSharedWorkspace(req: SharedWorkspaceCreateReq): Promise<SharedWorkspace> {
  const response = await apiClient.post<SharedWorkspace>(
    apiRoutes.admin.trainingSharedWorkspaces,
    req,
  );
  return response.data;
}

// 공용 등록 해제. registry 행만 지운다. 상류 workspace 와 이미 만든 Schedule 은 그대로 둔다.
export async function removeSharedWorkspace(trainingWorkspaceId: string): Promise<void> {
  await apiClient.delete(apiRoutes.admin.trainingSharedWorkspaceDetail(trainingWorkspaceId));
}

// 이미 다른 member 가 선점한 workspace 에 선점을 부여했다(409). 선점은 배타적이라 workspace 당
// 1명이다.
export function isWorkspaceAlreadyClaimedError(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "training_workspace.already_claimed";
}

// 등록 group 에 없거나 training 에서 사라진 workspace 를 조작했다(422). remap 이면 선점 해제로
// 안내한다.
export function isWorkspaceNotInScopeError(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "training_workspace.not_in_scope";
}

// 선점되지 않은 workspace 를 remap 했다(404).
export function isWorkspaceClaimNotFoundError(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "training_workspace_member.not_found";
}

// 대상 member 가 없다(404). 예: 소유자 이전에서 고른 member 가 사라진 경우.
export function isMemberNotFoundError(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "member.not_found";
}
